'use client';

import { useQueries, useQuery } from '@tanstack/react-query';
import { getTokenPrices } from 'lib/price/utils';
import { deduplicateArray, isNullish } from 'lib/utils';
import {
  type AllowanceUpdateProperties,
  applyRevokeToAllowances,
  applyUpdateToAllowances,
  getAllowancesFromEvents,
  stripAllowanceData,
  type TokenAllowanceData,
} from 'lib/utils/allowances';
import analytics from 'lib/utils/analytics';
import { getEventKey, type TokenEvent } from 'lib/utils/events';
import { MINUTE } from 'lib/utils/time';
import { hasZeroBalance, isErc721Contract } from 'lib/utils/tokens';
import { getSpenderData } from 'lib/utils/whois';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { Address } from 'viem';
import { usePublicClient } from 'wagmi';
import { queryClient } from '../QueryProvider';

export const useAllowances = (address: Address, events: TokenEvent[] | undefined, chainId: number) => {
  const publicClient = usePublicClient({ chainId })!;

  // Core allowances query (non-blocking, pricing set to null)
  const { data, isLoading, error } = useQuery<TokenAllowanceData[], Error>({
    queryKey: ['allowances', address, chainId, events?.map(getEventKey)],
    queryFn: async () => {
      const allowances = getAllowancesFromEvents(address, events!, publicClient, chainId);
      analytics.track('Fetched Allowances', { account: address, chainId });
      return allowances;
    },
    // If events (transfers + approvals) don't change, derived allowances also shouldn't change, even if allowances
    // are used on-chain. The only exception would be incorrectly implemented tokens that don't emit correct events
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !isNullish(address) && !isNullish(chainId) && !isNullish(events),
  });

  const [baseAllowances, setBaseAllowances] = useState<TokenAllowanceData[] | undefined>(undefined);

  // When the chainId changes, we reset the allowances, so it doesn't keep old allowances for the previous chain while loading
  // biome-ignore lint/correctness/useExhaustiveDependencies(chainId): We want this to re-run when chainId changes
  useEffect(() => {
    setBaseAllowances(undefined);
  }, [chainId]);

  useLayoutEffect(() => {
    if (data) {
      setBaseAllowances(data);
    }
  }, [data]);

  const uniqueErc20TokenAddresses = useMemo(() => {
    const erc20Addresses = (baseAllowances ?? [])
      .filter((allowance) => !isErc721Contract(allowance.contract))
      .map((allowance) => allowance.contract.address);

    return deduplicateArray(erc20Addresses).sort();
  }, [baseAllowances]);

  const priceQuery = useQuery<Record<Address, number | null>, Error>({
    queryKey: ['tokenPrices', chainId, uniqueErc20TokenAddresses],
    queryFn: () => getTokenPrices(chainId, uniqueErc20TokenAddresses),
    staleTime: 5 * MINUTE,
    refetchOnWindowFocus: false,
    placeholderData: undefined,
    enabled: uniqueErc20TokenAddresses.length > 0,
  });

  const spenderQueries = useQueries({
    queries: (baseAllowances ?? []).map((allowance) => ({
      queryKey: ['spenderData', allowance.chainId, allowance.payload?.spender ?? 'null'],
      queryFn: allowance.payload?.spender
        ? () => getSpenderData(allowance.payload!.spender, allowance.chainId)
        : () => null,
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false,
      placeholderData: undefined,
    })),
  });

  // Stable list of all owned tokens (for use by usePermitTokens, independent of approval state)
  const ownedTokens = useMemo(() => {
    if (!data) return undefined;
    return deduplicateArray(data, (a) => a.contract.address)
      .filter((token) => !hasZeroBalance(token.balance, token.metadata.decimals))
      .map(stripAllowanceData);
  }, [data]);

  const allowances = useMemo(() => {
    if (!baseAllowances) return undefined;

    return baseAllowances
      .map((allowance, index) => {
        const tokenPrice = isErc721Contract(allowance.contract)
          ? null
          : (priceQuery.data?.[allowance.contract.address as Address] ?? null);

        const metadata = { ...allowance.metadata, price: tokenPrice };
        const payload = allowance.payload
          ? { ...allowance.payload, spenderData: spenderQueries[index]?.data }
          : undefined;

        return { ...allowance, metadata, payload };
      })
      .filter((allowance) => !isNullish(allowance.payload));
  }, [baseAllowances, priceQuery.data, spenderQueries]);

  const onUpdate = async (allowance: TokenAllowanceData, updatedProperties: AllowanceUpdateProperties = {}) => {
    console.debug('Reloading data');

    // Invalidate blockNumber query, which triggers a refetch of the events, which in turn triggers a refetch of the allowances
    // We do not immediately refetch the allowances here, but we want to make sure that allowances will be refetched when
    // users navigate to the allowances page again
    await queryClient.invalidateQueries({
      queryKey: ['blockNumber', chainId],
      refetchType: 'none',
    });

    // Update in-memory state immediately
    if (!updatedProperties.amount || updatedProperties.amount === 0n) {
      setBaseAllowances((prev) => (prev ? applyRevokeToAllowances(prev, allowance) : prev));
    } else {
      setBaseAllowances((prev) => (prev ? applyUpdateToAllowances(prev, allowance, updatedProperties) : prev));
    }
  };

  return { allowances, ownedTokens, isLoading, error, onUpdate };
};
